/**
 * MatchedOpponentCard — Displays a matchmaking-selected opponent.
 * No browsing, no shopping. The system presents opponents within the user's strength band.
 */

import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Swords, Info, Globe, MapPin } from 'lucide-react-native';
import type { MatchCandidate } from '@/lib/matchmaking';

interface Props {
  candidate: MatchCandidate;
  onChallenge: (candidate: MatchCandidate) => void;
}

export default function MatchedOpponentCard({ candidate, onChallenge }: Props) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const isCrossGym = !candidate.gym_id || !profile?.gym_id || candidate.gym_id !== profile.gym_id;

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
      <View style={styles.header}>
        <View style={styles.identity}>
          <View style={styles.nameRow}>
            <Text style={[styles.name, { color: theme.colors.text }]}>
              {candidate.display_name || candidate.handle || 'Anonymous'}
            </Text>
            {isCrossGym ? (
              <View style={[styles.gymBadge, { backgroundColor: theme.colors.accent + '20' }]}>
                <Globe size={10} color={theme.colors.accent} />
                <Text style={[styles.gymBadgeText, { color: theme.colors.accent }]}>Cross-Gym</Text>
              </View>
            ) : (
              <View style={[styles.gymBadge, { backgroundColor: theme.colors.success + '20' }]}>
                <MapPin size={10} color={theme.colors.success} />
                <Text style={[styles.gymBadgeText, { color: theme.colors.success }]}>Same Gym</Text>
              </View>
            )}
          </View>
          {candidate.handle && (
            <Text style={[styles.handle, { color: theme.colors.textSecondary }]}>
              @{candidate.handle}
            </Text>
          )}
        </View>
        <View style={[styles.scoreBadge, { backgroundColor: theme.colors.background }]}>
          <Text style={[styles.scoreText, { color: theme.colors.accent }]}>
            {Math.round(candidate.lifting_score)}
          </Text>
        </View>
      </View>

      <View style={styles.reason}>
        <Info size={12} color={theme.colors.textTertiary} />
        <Text style={[styles.reasonText, { color: theme.colors.textTertiary }]}>
          {candidate.matchReason}
        </Text>
      </View>

      <TouchableOpacity
        style={[styles.challengeButton, { backgroundColor: theme.colors.accent }]}
        onPress={() => onChallenge(candidate)}>
        <Swords size={16} color={theme.colors.accentForeground} />
        <Text style={[styles.challengeText, { color: theme.colors.accentForeground }]}>Challenge</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  identity: {
    gap: 2,
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  name: {
    fontSize: 16,
    fontWeight: '700',
  },
  gymBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 2,
  },
  gymBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  handle: {
    fontSize: 13,
    fontWeight: '500',
  },
  scoreBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 2,
  },
  scoreText: {
    fontSize: 16,
    fontWeight: '800',
  },
  reason: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  reasonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  challengeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 2,
  },
  challengeText: {
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
});
